import { useState, useCallback, useEffect } from 'react';
import i18n from '../i18n';
import { env } from '../shared/utils/env';

// E-IMZO WebSocket URL
const EIMZO_WS_URL = window.location.protocol.toLowerCase() === "https:"
  ? "wss://127.0.0.1:64443/service/cryptapi"
  : "ws://127.0.0.1:64646/service/cryptapi";

// E-IMZO API keys are bound to the EXACT hostname the page is served from:
// the desktop module answers every plugin call with -1022 "API-key для
// домена недействителен" when the current host has no key, which the UI
// surfaces as "no certificates found". The built-in list covers only the
// E-IMZO public test hosts (local development); every deployed host's key is
// runtime config — VITE_EIMZO_API_KEYS="host:KEY,host:KEY" in the chart's env,
// written into /env-config.js at container start — so a new domain is a
// values change, not a rebuild.
const BUILTIN_API_KEYS = [
  'localhost', '96D0C1491615C82B9A54D9989779DF825B690748224C2B04F500F370D51827CE2644D8D4A82C18184D73AB8530BB8ED537269603F61DB0D03D2104ABF789970B',
  '127.0.0.1', 'A7BCFA5D490B351BE0754130DF03A068F855DB4333D43921125B9CF2670EF6A40370C646B90401955E1F7BC9CDBF59CE0B2C5467D820BE189C845D0B79CFC96F',
];
const CONFIGURED_API_KEYS = String(env('VITE_EIMZO_API_KEYS') || '')
  .split(',')
  .map((pair) => pair.trim())
  .filter(Boolean)
  .flatMap((pair) => {
    const idx = pair.indexOf(':');
    return idx > 0 ? [pair.slice(0, idx).trim(), pair.slice(idx + 1).trim()] : [];
  });
const API_KEYS = [...BUILTIN_API_KEYS, ...CONFIGURED_API_KEYS];

// Whether the module has a key for THIS host — used to tell "no key for this
// domain" apart from "no certificates" in the setup UI.
export const eimzoHostHasApiKey = () => {
  const host = window.location.hostname;
  for (let i = 0; i < API_KEYS.length; i += 2) if (API_KEYS[i] === host) return true;
  return false;
};

/**
 * Call E-IMZO function - creates a new WebSocket for each call
 * This matches the official E-IMZO API pattern
 * @param {Object} funcDef - The function definition to send
 * @param {number|null} timeoutMs - Timeout in ms, null for no timeout (for operations requiring user input)
 */
const callEIMZO = (funcDef, timeoutMs = 30000) => {
  return new Promise((resolve, reject) => {
    if (!window.WebSocket) {
      reject(new Error('WebSocket not supported'));
      return;
    }

    let socket;
    try {
      socket = new WebSocket(EIMZO_WS_URL);
    } catch (e) {
      reject(e);
      return;
    }

    let timeout = null;
    if (timeoutMs !== null) {
      timeout = setTimeout(() => {
        socket.close();
        reject(new Error(i18n.t('signing:eimzo.errors.connectionTimeout')));
      }, timeoutMs);
    }

    socket.onclose = (e) => {
      clearTimeout(timeout);
      if (e.code !== 1000) {
        reject(new Error(`WebSocket closed with code ${e.code}`));
      }
    };

    socket.onmessage = (event) => {
      clearTimeout(timeout);
      try {
        const data = JSON.parse(event.data);
        socket.close();
        resolve(data);
      } catch (e) {
        socket.close();
        reject(e);
      }
    };

    socket.onerror = (e) => {
      clearTimeout(timeout);
      reject(new Error('WebSocket error'));
    };

    socket.onopen = () => {
      console.log('E-IMZO sending:', JSON.stringify(funcDef));
      socket.send(JSON.stringify(funcDef));
    };
  });
};

/**
 * Side-effect-free availability probe: one `version` call with a short
 * timeout. Unlike connect(), it installs no API keys, so it is safe to fire
 * ambiently (status dots) without touching the client's key store. Resolves
 * {major, minor} when the desktop client answers; rejects fast when it is
 * not installed / not running.
 */
export const probeEImzoVersion = async () => {
  const data = await callEIMZO({ name: 'version' }, 2500);
  if (!data?.success) {
    throw new Error(data?.reason || 'E-IMZO version check failed');
  }
  return { major: data.major, minor: data.minor };
};

/**
 * Parse X500 value from certificate alias
 */
const getX500Val = (s, f) => {
  const res = s.split(/,(?=[A-Z]+=)/g);
  for (const part of res) {
    const idx = part.indexOf(f + '=');
    if (idx !== -1) {
      return part.slice(idx + f.length + 1);
    }
  }
  return '';
};

/**
 * Hook for E-IMZO digital signature integration
 */
export const useEIMZO = () => {
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [error, setError] = useState(null);
  const [loadedKeyId, setLoadedKeyId] = useState(null);
  const [apiVersion, setApiVersion] = useState(null);

  // Check E-IMZO version
  const checkVersion = useCallback(async () => {
    try {
      const data = await callEIMZO({ name: 'version' });
      console.log('E-IMZO version:', data);
      if (data.success) {
        const version = parseInt(data.major) * 100 + parseInt(data.minor);
        setApiVersion({ major: data.major, minor: data.minor, version });
        return { major: data.major, minor: data.minor, version };
      } else {
        throw new Error(data.reason || 'Failed to get version');
      }
    } catch (err) {
      console.error('E-IMZO version check failed:', err);
      throw err;
    }
  }, []);

  // Install API keys
  const installApiKeys = useCallback(async () => {
    try {
      const data = await callEIMZO({ name: 'apikey', arguments: API_KEYS });
      console.log('E-IMZO apikey result:', data);
      if (!data.success) {
        throw new Error(data.reason || 'Failed to install API keys');
      }
      return true;
    } catch (err) {
      console.error('E-IMZO API key installation failed:', err);
      throw err;
    }
  }, []);

  // Connect to E-IMZO (check version and install API keys)
  const connect = useCallback(async () => {
    if (connecting) return;

    setConnecting(true);
    setError(null);

    try {
      // Step 1: Check version
      const version = await checkVersion();
      console.log('E-IMZO version:', version);

      // Step 2: Install API keys
      await installApiKeys();

      setConnected(true);
      setConnecting(false);
      return true;
    } catch (err) {
      setError(err.message || i18n.t('signing:eimzo.errors.connectFailed'));
      setConnecting(false);
      throw err;
    }
  }, [connecting, checkVersion, installApiKeys]);

  // Disconnect
  const disconnect = useCallback(() => {
    setConnected(false);
    setCertificates([]);
    setSelectedCertificate(null);
    setLoadedKeyId(null);
  }, []);

  // Load all certificates (PFX files)
  const loadCertificates = useCallback(async () => {
    try {
      setError(null);
      const items = [];

      // List of plugins to check for certificates
      const plugins = ['pfx', 'ftclient', 'eri', 'pkcs11'];

      for (const pluginName of plugins) {
        try {
          console.log(`E-IMZO loading ${pluginName} certificates...`);
          const pluginData = await callEIMZO({ plugin: pluginName, name: 'list_all_certificates' });
          console.log(`E-IMZO ${pluginName} result:`, pluginData);

          if (pluginData.success && pluginData.certificates) {
            for (const el of pluginData.certificates) {
              // Parse the alias to extract certificate info
              let x500name = (el.alias || '').toUpperCase();
              x500name = x500name.replace('1.2.860.3.16.1.1=', 'INN=');
              x500name = x500name.replace('1.2.860.3.16.1.2=', 'PINFL=');

              // Extract serial number and lowercase it to match API
              const rawSerialNumber = getX500Val(x500name, 'SERIALNUMBER') ||
                getX500Val(x500name, 'SERIAL') ||
                '';

              const cert = {
                disk: el.disk,
                path: el.path,
                name: el.name,
                alias: el.alias,
                serialNumber: rawSerialNumber.toLowerCase(),
                CN: getX500Val(x500name, 'CN'),
                TIN: getX500Val(x500name, 'INN') || getX500Val(x500name, 'UID'),
                UID: getX500Val(x500name, 'UID'),
                PINFL: getX500Val(x500name, 'PINFL'),
                O: getX500Val(x500name, 'O'),
                type: pluginName,
                // Parse dates
                validFrom: null,
                validTo: null,
                isExpired: false,
              };

              // Parse validity dates
              const validFromStr = getX500Val(x500name, 'VALIDFROM');
              const validToStr = getX500Val(x500name, 'VALIDTO');
              if (validFromStr) {
                cert.validFrom = new Date(validFromStr.replace(/\./g, '-').replace(' ', 'T'));
              }
              if (validToStr) {
                // Handle various date formats (DD.MM.YYYY HH:mm:ss or YYYY-MM-DD HH:mm:ss)
                const normalizedDate = validToStr.replace(/\./g, '-').replace(' ', 'T');
                cert.validTo = new Date(normalizedDate);
                cert.isExpired = cert.validTo < new Date();
              }

              // Extract PINFL for display
              cert.pinfl = cert.PINFL || null;
              cert.fullName = cert.CN || cert.name || 'Unknown';
              cert.organization = cert.O || '';
              cert.id = `${pluginName}-${el.disk || ''}-${el.path || ''}-${el.name || el.serialNumber}`;

              items.push(cert);
            }
          }
        } catch (pluginErr) {
          // ERI plugin requires an API key for the specific domain
          // Other plugins (pfx, ftclient, pkcs11) should work without issues
          if (pluginName === 'eri') {
            console.info(`Skipping ${pluginName} certificates - API key may not be configured for this domain`);
          } else {
            console.warn(`Failed to load certificates for plugin ${pluginName}:`, pluginErr);
          }
        }
      }

      console.log('E-IMZO loaded certificates:', items);
      setCertificates(items);
      return items;
    } catch (err) {
      console.error('E-IMZO load certificates error:', err);
      setError(err.message || i18n.t('signing:eimzo.errors.loadCertificatesFailed'));
      throw err;
    }
  }, []);

  // Load key - E-IMZO will prompt for password via its own UI
  const loadKey = useCallback(async (certificate) => {
    try {
      setError(null);

      const validPlugins = ['pfx', 'ftclient', 'eri', 'pkcs11'];
      if (validPlugins.includes(certificate.type)) {
        // For PFX/FTCLIENT/ERI: load_key(disk, path, name, alias)
        const data = await callEIMZO({
          plugin: certificate.type,
          name: 'load_key',
          arguments: [certificate.disk, certificate.path, certificate.name, certificate.alias]
        });

        console.log('E-IMZO load_key result:', data);

        if (data.success) {
          setLoadedKeyId(data.keyId);
          return data.keyId;
        } else {
          throw new Error(data.reason || i18n.t('signing:eimzo.errors.loadKeyFailed'));
        }
      } else {
        throw new Error(
          i18n.t('signing:eimzo.errors.unsupportedCertificateType', { type: certificate.type }),
        );
      }
    } catch (err) {
      console.error('E-IMZO loadKey error:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Create PKCS7 signature
  const signData = useCallback(async (dataToSign, keyId, isAlreadyBase64 = false) => {
    try {
      setError(null);

      const actualKeyId = keyId || loadedKeyId;
      if (!actualKeyId) {
        throw new Error(i18n.t('signing:eimzo.errors.noKeyLoaded'));
      }

      // Base64 encode the data if it's not already encoded
      const data64 = (typeof dataToSign === 'string' && !isAlreadyBase64)
        ? btoa(unescape(encodeURIComponent(dataToSign)))
        : dataToSign;

      // Use null timeout - create_pkcs7 waits for user to enter password in E-IMZO dialog
      const data = await callEIMZO({
        plugin: 'pkcs7',
        name: 'create_pkcs7',
        arguments: [data64, actualKeyId, 'no'] // 'no' = attached signature
      }, null);

      console.log('E-IMZO create_pkcs7 result:', data);

      if (data.success) {
        return data.pkcs7_64;
      } else {
        throw new Error(data.reason || i18n.t('signing:eimzo.errors.createSignatureFailed'));
      }
    } catch (err) {
      console.error('E-IMZO signData error:', err);
      setError(err.message);
      throw err;
    }
  }, [loadedKeyId]);

  return {
    // State
    connected,
    connecting,
    certificates,
    selectedCertificate,
    error,
    loadedKeyId,
    apiVersion,

    // Actions
    connect,
    disconnect,
    loadCertificates,
    loadKey,
    signData,
    setSelectedCertificate,
  };
};

export default useEIMZO;
